/** Billing helpers. Cards themselves are collected by Stripe's Payment Element (see `lib/stripe.ts`). */
import type { PaymentMethod } from "./types";

/**
 * Whether a saved card can pay for something the person buys on the page now: not expired, and not
 * a saved Apple Pay card (Apple Pay's terms keep those for renewals; the API refuses them).
 */
export const payableNow = (m: PaymentMethod): boolean => !m.expired && !m.renewals_only;

/**
 * Whether a saved method can pay for a plan upgrade: what `payableNow` allows, or a bank account whose
 * Direct Debit isn't cancelled (the upgrade then starts once the debit clears).
 */
export const payableForUpgrade = (m: PaymentMethod): boolean =>
  payableNow(m) || (m.kind === "bacs_debit" && m.mandate_status !== "inactive");

/** A saved method that can't be charged as it is: an expired card, or a cancelled Direct Debit. */
export const methodProblem = (m: PaymentMethod): boolean =>
  m.expired || (m.kind === "bacs_debit" && m.mandate_status === "inactive");

/**
 * The line under a saved method: a card's expiry ("Expires 09/30"), or how its Direct Debit stands.
 * `renewals`: add " · Used for renewals" to an Apple Pay card.
 */
export function methodDetail(m: PaymentMethod, { renewals = false }: { renewals?: boolean } = {}): string {
  if (m.kind === "bacs_debit") {
    if (m.mandate_status === "inactive") return "Direct Debit cancelled";
    return m.mandate_status === "pending" ? "Direct Debit being set up" : "Direct Debit active";
  }
  const expiry = `${String(m.exp_month ?? 0).padStart(2, "0")}/${String(m.exp_year ?? "").slice(-2)}`;
  return `${m.expired ? "Expired" : "Expires"} ${expiry}${renewals && m.renewals_only ? " · Used for renewals" : ""}`;
}

/** The card a payment made on the page starts on: the default if it can pay now, else the first that can. */
export function payableDefault(methods: PaymentMethod[] | undefined): PaymentMethod | undefined {
  const payable = (methods ?? []).filter(payableNow);
  return payable.find((m) => m.is_default) ?? payable[0];
}

/** Errors shown with the card form: `card` is Stripe's form ("" = Stripe already marks its fields). */
export interface CardErrors {
  card?: string;
}

/** Who processes payments, for the "Processed by …" lines. */
export function processorName(provider: string): string {
  return provider === "stripe" ? "Stripe" : "CortexGrip test payments";
}

/** ISO 3166-1 alpha-2 codes offered on the billing address. */
export const COUNTRIES = [
  "GB",
  "IE",
  "US",
  "CA",
  "AU",
  "NZ",
  "DE",
  "FR",
  "ES",
  "IT",
  "NL",
  "BE",
  "PT",
  "SE",
  "NO",
  "DK",
  "FI",
  "PL",
  "CH",
  "AT",
  "BD",
  "IN",
  "PK",
  "AE",
  "SG",
  "ZA",
  "NG",
  "KE",
  "JP",
  "BR",
  "MX",
];

const regionNames = new Intl.DisplayNames(undefined, { type: "region" });
export const countryName = (code: string): string => regionNames.of(code) ?? code;
